<template>
  <div>
    <ListView
      title="通知"
      :isFluid="true"
      @reload="reload"
      @navDetails="actionDetails"
      :search.sync="search"
      :data="data"
      :page.sync="page"
      :header="header"
      :enable-create="false"
      @create="actionCreate"
    >
      <template #searchBox>
        <span />
      </template>
      <template #filter>
        <v-row dense class="filter-row">
          <v-col cols="auto">
            <v-text-field
              v-model="pendingFilters.targetName"
              dense
              outlined
              hide-details
              label="接收者姓名"
              prepend-inner-icon="mdi-account"
              clearable
              style="min-width: 200px"
              @keyup.enter="applyFilters"
            />
          </v-col>
          <v-col cols="auto">
            <v-text-field
              v-model="pendingFilters.targetPhone"
              dense
              outlined
              hide-details
              label="接收者電話"
              prepend-inner-icon="mdi-phone"
              clearable
              style="min-width: 200px"
              @keyup.enter="applyFilters"
            />
          </v-col>
          <v-col cols="auto">
            <v-text-field
              v-model="pendingSearchVal"
              dense
              outlined
              hide-details
              label="內容"
              prepend-inner-icon="mdi-text"
              clearable
              style="min-width: 200px"
              @keyup.enter="applyFilters"
            />
          </v-col>
          <v-col cols="auto">
            <v-menu
              ref="dateStartMenu"
              v-model="dateStartMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="pendingFilters.dateStart"
                  label="發送日期 開始"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  dense
                  outlined
                  hide-details
                  clearable
                  style="min-width: 200px"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="pendingFilters.dateStart"
                @input="dateStartMenu = false"
                no-title
                scrollable
              />
            </v-menu>
          </v-col>
          <v-col cols="auto">
            <v-menu
              ref="dateEndMenu"
              v-model="dateEndMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="pendingFilters.dateEnd"
                  label="發送日期 截止"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  dense
                  outlined
                  hide-details
                  clearable
                  style="min-width: 200px"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="pendingFilters.dateEnd"
                @input="dateEndMenu = false"
                no-title
                scrollable
              />
            </v-menu>
          </v-col>
          <v-col cols="auto" class="d-flex align-center gap-2">
            <v-btn color="primary" @click="applyFilters" :loading="isLoading" :disabled="isLoading">
              <v-icon left>mdi-magnify</v-icon>
              搜尋
            </v-btn>
            <v-btn color="default" @click="resetFilters" :disabled="isLoading">
              <v-icon left>mdi-refresh</v-icon>
              清除選項
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item._targetName="{ item }">
        <span>{{ get(item, 'metadata.targetName', '') }}</span>
        <span v-if="get(item, 'metadata.targetType', '') === 'AGENT'" class="text-caption">
          (代理人)
        </span>
      </template>
      <template v-slot:item._targetPhone="{ item }">
        <div>{{ get(item, 'metadata.targetPhone', '') }}</div>
      </template>
      <template v-slot:item.title="{ item }">
        <div class="font-weight-medium">{{ item.title }}</div>
      </template>

      <template v-slot:item.message="{ item }">
        <div style="max-width: 400px" class="d-inline-block text-truncate">
          {{ item.message }}
        </div>
      </template>

      <template v-slot:item.isRead="{ item }">
        <v-chip outlined :color="item.isRead ? 'grey' : 'primary'" label small>
          {{ item.isRead ? 'Read' : 'Unread' }}
        </v-chip>
      </template>

      <template v-slot:item.readAt="{ item }">
        <div v-if="item.readAt">
          {{ item.readAt | moment }}
        </div>
        <div v-else>-</div>
      </template>

      <template v-slot:item.expiresAt="{ item }">
        <div v-if="item.expiresAt" :class="{ 'red--text': isExpired(item.expiresAt) }">
          {{ item.expiresAt | moment }}
        </div>
        <div v-else>Never</div>
      </template>

      <template v-slot:item.createdBy="{ item }">
        <span>{{ get(item, 'createdBy.name', 'System') }}</span>
      </template>

      <template v-slot:item.createdAt="{ item }">
        {{ item.createdAt | moment }}
      </template>

      <template v-slot:item._type="{ item }">
        <div v-if="get(item, 'metadata.crmId', false)" class="d-flex align-center gap-2">
          <div>CRM</div>
          <div>
            <v-chip label small color="primary">{{ item.metadata.N1 }}</v-chip>
          </div>
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn x-small text color="primary" @click.stop="openDetails(item)">
          <v-icon small>mdi-eye</v-icon>
        </v-btn>
      </template>
    </ListView>

    <!-- Notification Details Dialog -->
    <v-dialog v-model="dialog" max-width="700">
      <v-card v-if="selectedItem">
        <v-card-title class="text-h6 primary white--text">
          通知詳情
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="text-caption grey--text">接收者姓名</div>
              <div class="text-body-1 mb-3">
                {{ get(selectedItem, 'metadata.targetName', '-') }}
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-caption grey--text">接收者電話</div>
              <div class="text-body-1 mb-3">
                {{ get(selectedItem, 'metadata.targetPhone', '-') }}
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-caption grey--text">類型</div>
              <div class="text-body-1 mb-3">
                <div
                  v-if="get(selectedItem, 'metadata.crmId', false)"
                  class="d-flex align-center gap-2"
                >
                  <div>CRM</div>
                  <div>
                    <v-chip label small color="primary">{{ selectedItem.metadata.N1 }}</v-chip>
                  </div>
                </div>
                <span v-else>-</span>
              </div>
            </v-col>

            <v-col cols="12">
              <div class="text-caption grey--text">標題</div>
              <div class="text-body-1 font-weight-medium mb-3">
                {{ selectedItem.title || '-' }}
              </div>
            </v-col>

            <v-col cols="12">
              <div class="text-caption grey--text">內容</div>
              <div class="text-body-1 mb-3" style="white-space: pre-wrap">{{ selectedItem.message || '-' }}</div>
            </v-col>

            <v-col v-if="notificationAttachments.length" cols="12">
              <div class="text-caption grey--text">附件</div>
              <v-simple-table dense class="mb-3 notification-attachments-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">檔案名稱</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="attachment in notificationAttachments"
                      :key="attachment.id || attachment.filename"
                    >
                      <td>{{ attachment.filename }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-caption grey--text">安排發送時間</div>
              <div class="text-body-1 mb-3">
                {{ selectedItem.createdAt | moment }}
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-caption grey--text">創建者</div>
              <div class="text-body-1 mb-3">
                {{ get(selectedItem, 'createdBy.name', 'System') }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { RESTFUL, notificationType } from '@/data/constants';
import listViewPageMixin from '@/services/listViewPageMixin';
import { mapGetters } from 'vuex';
import { get } from 'lodash';
import Autocomplete from '@/components/Autocomplete/Autocomplete';
import api from '@/api';
import { notificationTypeOption } from '@/data/optionsConstants';
import dayjs from 'dayjs';

export default {
  name: 'Notifications',
  components: {
    Autocomplete,
  },
  data() {
    return {
      notificationType,
      notificationTypeOption,
      header: [
        { text: '接收者姓名', value: '_targetName' },
        { text: '接收者電話', value: '_targetPhone' },
        { text: '類型', value: '_type' },
        { text: '標題', value: 'title' },
        { text: '內容', value: 'message' },
        // { text: 'Status', value: 'isRead' },
        // { text: 'Read At', value: 'readAt' },
        // { text: 'Expires At', value: 'expiresAt' },
        // { text: 'Created By', value: 'createdBy' },
        { text: '安排發送時間', value: 'createdAt' },
        { text: '操作', value: 'actions', sortable: false },
      ],
      // populate: ['target', 'createdBy'],
      selectedTypeFilter: null,
      selectedStatusFilter: null,
      filterTarget: null,
      dialog: false,
      selectedItem: null,
      // Filter data
      filters: {
        targetName: null,
        targetPhone: null,
        dateStart: null,
        dateEnd: null,
      },
      pendingFilters: {
        targetName: null,
        targetPhone: null,
        dateStart: null,
        dateEnd: null,
      },
      dateStartMenu: false,
      dateEndMenu: false,
      pendingSearchVal: '',
    };
  },
  mixins: [listViewPageMixin],
  computed: {
    ...mapGetters('auth', ['role', 'isMaster', 'isAdmin']),
    restfulURL() {
      return RESTFUL.notifications.list;
    },
    searchPaths() {
      return ['message'];
    },
    filter() {
      const filter = {};

      // Name filter
      if (this.filters.targetName) {
        filter['metadata.targetName'] = { $regex: this.filters.targetName, $options: 'i' };
      }

      // Phone filter
      if (this.filters.targetPhone) {
        filter['metadata.targetPhone'] = { $regex: this.filters.targetPhone, $options: 'i' };
      }

      // Date range filter
      if (this.filters.dateStart || this.filters.dateEnd) {
        filter.createdAt = {};
        if (this.filters.dateStart) {
          filter.createdAt.$gte = dayjs(this.filters.dateStart).startOf('day').toDate();
        }
        if (this.filters.dateEnd) {
          filter.createdAt.$lte = dayjs(this.filters.dateEnd).endOf('day').toDate();
        }
      }

      return Object.keys(filter).length > 0 ? filter : null;
    },
    typeFilters() {
      return notificationTypeOption;
    },
    statusFilters() {
      return [
        { label: 'Unread', value: 'unread' },
        { label: 'Read', value: 'read' },
      ];
    },
    notificationAttachments() {
      const raw = get(this.selectedItem, 'metadata.attachments', []);
      if (!Array.isArray(raw)) {
        return [];
      }
      return raw
        .map((item, index) => {
          if (!item || typeof item !== 'object') {
            return null;
          }
          const id = item.id || item._id || `attachment-${index}`;
          const filename = item.filename || item.name;
          if (!filename) {
            return null;
          }
          return { id: String(id), filename: String(filename) };
        })
        .filter(Boolean);
    },
  },
  methods: {
    get,
    actionCreate() {
      this.$router.push({ name: 'notificationNew' });
    },
    getTypeColor(type) {
      const colorMap = {
        [notificationType.info]: 'info',
        [notificationType.warning]: 'warning',
        [notificationType.error]: 'error',
        [notificationType.success]: 'success',
      };
      return colorMap[type] || 'grey';
    },
    getTypeIcon(type) {
      const iconMap = {
        [notificationType.info]: 'mdi-information',
        [notificationType.warning]: 'mdi-alert',
        [notificationType.error]: 'mdi-alert-circle',
        [notificationType.success]: 'mdi-check-circle',
      };
      return iconMap[type] || 'mdi-bell';
    },
    isExpired(expiresAt) {
      return new Date(expiresAt) < new Date();
    },
    async markAsRead(item) {
      try {
        await api.patch(`${RESTFUL.notifications.update}/${item._id}`, {
          isRead: true,
          readAt: new Date(),
        });
        this.$toast.success('Notification marked as read');
        this.dialog = false;
        this.reload();
      } catch (error) {
        this.$toast.error('Failed to mark notification as read');
      }
    },
    async deleteNotification(item) {
      if (!confirm('Are you sure you want to delete this notification?')) return;
      try {
        await api.delete(`${RESTFUL.notifications.delete}/${item._id}`);
        this.$toast.success('Notification deleted');
        this.dialog = false;
        this.reload();
      } catch (error) {
        this.$toast.error('Failed to delete notification');
      }
    },
    actionDetails(item) {
      // this.selectedItem = item;
      // this.dialog = true;
    },
    openDetails(item) {
      this.selectedItem = item;
      this.dialog = true;
    },
    applyFilters() {
      // Apply pending values to actual filter values
      this.search = this.pendingSearchVal || '';
      this.filters = { ...this.pendingFilters };
      this.debouncedSearch();
    },
    resetFilters() {
      // Reset pending filters
      this.pendingSearchVal = '';
      this.pendingFilters = {
        targetName: null,
        targetPhone: null,
        dateStart: null,
        dateEnd: null,
      };
    },
  },
};
</script>
